import { Divider } from 'app/pages/admin/index.styled';
import { Dropdown, LoadingIndicator, NewModal, Search } from 'lib/components';
import { Flex, Gap } from 'lib/components/styles/layout';
import { ParagraphNormal } from 'lib/components/styles/typography';
import { DEFAULT_PAGE, DEFAULT_SIZE } from 'lib/const/PaginationConstants';
import { VideoListAutomotiveItem } from 'lib/context';
import React, { useState } from 'react';
import { VideoItem } from '../components/VideoItem';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInfiniteQueryVideos } from 'lib/api/videos/useInfiniteQueryVideos';
import { Folder, useGetFolders } from 'lib/api/folders/getFolders';
import { ALL_FOLDERS, DEFAULT_FOLDER } from 'lib/const/Folders';
import { Button } from 'react-covideo-common';
import { Label } from 'lib/components/form/label';
import { IVideoDetails, QuickVideoPartType } from 'lib/api/quickVideo/types';
import { capitalizeWords } from 'lib/utils/functions';

type AddVideoModalProps = {
  onClose: () => void;
  onAddVideo: (video: IVideoDetails) => void;
  type: QuickVideoPartType | '';
};

export const AddVideoModal = ({
  onClose,
  onAddVideo,
  type,
}: AddVideoModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<{
    value: number;
    label: string;
  }>({ value: DEFAULT_FOLDER.folderId, label: DEFAULT_FOLDER.name });
  const [selectedVideo, setSelectedVideo] = useState<IVideoDetails | null>(
    null
  );

  const { data: folders = [], isLoading: areFoldersLoading } = useGetFolders();

  const videoFilter = {
    videoType: '',
    advisorId: '',
    userId: '',
    status: '',
    emailStatus: '',
    folder:
      selectedFolder.label === ALL_FOLDERS.label
        ? ALL_FOLDERS.value
        : selectedFolder.label,
    page: DEFAULT_PAGE,
    size: DEFAULT_SIZE,
    search: searchQuery,
  };

  const {
    data: videosInfiniteData,
    fetchNextPage,
    isLoading: areVideosLoading,
  } = useInfiniteQueryVideos(videoFilter);

  const count = +(videosInfiniteData?.pages?.[0]?.count ?? 0);

  const videos =
    videosInfiniteData?.pages.flatMap(page =>
      page.videos.map(item => ({
        ...item,
      }))
    ) ?? [];

  const areFieldsDisabled = areFoldersLoading || areVideosLoading;
  const folderOptions = folders.map((folder: Folder) => ({
    value: folder.folderId,
    label: folder.name,
  }));

  const videoPartTypeName = capitalizeWords(
    `${type.toString().replaceAll('_', ' ')}`
  );

  const addVideo = (selectedVideo: IVideoDetails) => {
    onAddVideo(selectedVideo);
    onClose();
  };

  const getEndSearchResultMessage = () => {
    if (!areVideosLoading && !videos.length) {
      return 'No videos under the selected folder.';
    }

    return "You've reached the end.";
  };

  return (
    <NewModal
      style={{
        content: { minWidth: '688px' },
      }}
      closeModal={onClose}
      headerText={`${videoPartTypeName} - Add Video`}
    >
      <Gap gap='20px'>
        <ParagraphNormal>
          {`Select ${videoPartTypeName} to use in Quick Video.`}
        </ParagraphNormal>

        <Flex justifyContent='space-between' gap='8px' flexDirection='row'>
          <Gap gap='8px' width='100%'>
            <Label label='Search' />
            <Search
              placeholder='Search Videos'
              onSearch={(search: string) => setSearchQuery(search)}
              prevSearch={searchQuery}
              disabled={areFieldsDisabled}
              bgColor='white'
            />
          </Gap>
          <Gap gap='8px' flexDirection='column' alignItems='flex-start'>
            <Label label='Select Folder:' />
            <Dropdown
              width={235}
              options={folderOptions}
              value={selectedFolder}
              onChange={val => {
                setSelectedFolder(val);
              }}
              disabled={areFieldsDisabled}
              creatable={false}
              placeholder='Select Folder'
            />
          </Gap>
        </Flex>

        <Divider marginBottom='0' marginTop='0' />

        <InfiniteScroll
          hasMore={videos.length < count}
          next={fetchNextPage}
          dataLength={videos.length}
          height={220}
          style={{
            gap: '17px',
            flexDirection: 'column',
            display: 'flex',
            width: '690px',
          }}
          loader={<h4>Loading...</h4>}
          endMessage={
            !areVideosLoading && (
              <ParagraphNormal>{getEndSearchResultMessage()}</ParagraphNormal>
            )
          }
        >
          {areVideosLoading ? (
            <LoadingIndicator isLoading height='200px' />
          ) : (
            videos.map((video: VideoListAutomotiveItem) => {
              return (
                <VideoItem
                  key={video.id}
                  selectedVideo={selectedVideo}
                  setSelectedVideo={setSelectedVideo}
                  video={video}
                />
              );
            })
          )}
        </InfiniteScroll>

        <Divider marginBottom='0' marginTop='0' />

        <Gap
          flexDirection='row'
          justifyContent='flex-end'
          gap='8px'
          width='100%'
        >
          <Button variant='secondary' onClick={onClose} text='Cancel' />
          <Button
            onClick={() => {
              if (selectedVideo) addVideo(selectedVideo);
            }}
            text='Add Video'
            disabled={!selectedVideo}
          />
        </Gap>
      </Gap>
    </NewModal>
  );
};
